import { useEffect } from 'react';

export const FIXED_ACTION_BAR_VAR = '--fixed-action-bar-h';

// Nearest position:fixed ancestor of a button inside root (the action bar).
// Found by computed style because each screen's withStyles gets its own class names.
const findFixedBar = (root) => {
  const buttons = root.querySelectorAll('button');
  for (let i = 0; i < buttons.length; i += 1) {
    for (let el = buttons[i]; el && el !== root; el = el.parentElement) {
      if (window.getComputedStyle(el).position === 'fixed') return el;
    }
  }
  return null;
};

// While a fixed action bar is rendered inside rootRef and isFixed is true,
// publish its height as --fixed-action-bar-h so the footer can reserve room.
export default function useFixedActionBarReserve(rootRef, isFixed) {
  useEffect(() => {
    const root = rootRef.current;
    const style = document.documentElement.style;
    if (!root || !isFixed) {
      style.removeProperty(FIXED_ACTION_BAR_VAR);
      return undefined;
    }

    let bar = null;
    // jsdom and old browsers lack these; we still measure on mount and resize.
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => sync()) : null;
    const mutationObserver =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(() => sync()) : null;

    function sync() {
      // Reuse the known bar while attached; rescanning every button is costly.
      const next = bar && root.contains(bar) ? bar : findFixedBar(root);
      if (next !== bar) {
        if (resizeObserver && bar) resizeObserver.unobserve(bar);
        bar = next;
        if (resizeObserver && bar) resizeObserver.observe(bar);
      }
      const height = bar ? Math.ceil(bar.getBoundingClientRect().height) : 0;
      if (height > 0) style.setProperty(FIXED_ACTION_BAR_VAR, `${height}px`);
      else style.removeProperty(FIXED_ACTION_BAR_VAR);
    }

    if (mutationObserver) mutationObserver.observe(root, { childList: true, subtree: true });
    window.addEventListener('resize', sync);
    sync();

    return () => {
      window.removeEventListener('resize', sync);
      if (mutationObserver) mutationObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      style.removeProperty(FIXED_ACTION_BAR_VAR);
    };
  }, [rootRef, isFixed]);
}
